import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { AGENCY_DOMAIN } from '../lib/demo.js';

// Session 68: the demo walkthrough, on Edit client for a "(Demo)" client.
//
// What the droplet does for a demo that it does not do for a real client
// (tools/lotus_demo.py, every reactor tick):
//   - approves the strategy the moment it is written, so it is ready before
//     the call (it starts when the client is created)
//   - sends the plan to the client the moment the Welcome screen is finished
//     (not before, so the plan lands right after onboarding on screen)
//   - releases the first draft to the client as soon as it is written
//   - holds the pack to fast start on and no agency draft gate
// Leena, signed in as the demo's client login in another window, approves
// the plan, answers the questions and sees the draft, like a client would.
//
// Reset demo: every job, package and picture goes, the Welcome screen goes
// back to pre-filled, the client goes back to onboarding and a fresh strategy
// is queued (about six minutes). The sign-in and the client's details stay.

export const DEMO_AUTOPILOT_HINT = 'The strategy is approved the moment it is written, the plan goes to the client the moment the Welcome screen is finished, and the first draft is released as soon as it is written. Nothing waits on the agency.';

export default function DemoPanel({ clientId, onReset }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  async function reset() {
    setBusy(true); setBanner(null);
    const { data, error } = await supabase.rpc('portal_demo_reset', { p_client_id: clientId });
    setBusy(false); setConfirming(false);
    if (error) { setBanner({ tone: 'err', text: error.message }); return; }
    setBanner({ tone: 'ok', text: `Reset queued (${data}). Within a minute the old plan, drafts and pictures are gone and the Welcome screen is back to the start; the new strategy is ready about six minutes later. Set up the demo at least ten minutes before the call.` });
    if (onReset) await onReset();
  }

  return (
    <section className="form-section demo-panel">
      <h2>Demo</h2>
      <p className="hint">
        Demo client: contacts must be @{AGENCY_DOMAIN} addresses and publishing stays on handoff, so a recording never reaches a real inbox or a real site.
        {' '}{DEMO_AUTOPILOT_HINT}
      </p>
      <p className="hint">
        On the call: sign in as the demo's client login in another window, walk through the Welcome screen (it arrives pre-filled from the website), approve the plan when it lands, answer the first post's questions, and open the draft when it arrives. Every email a client would get lands in the demo's inbox.
      </p>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      <div className="demo-actions">
        {!confirming ? (
          <button type="button" className="btn" disabled={busy} onClick={() => setConfirming(true)}>Reset demo</button>
        ) : (
          <>
            <span className="hint">Wipe this demo's plan, drafts, pictures and progress and start again from the pre-filled Welcome screen? The sign-in stays.</span>
            {' '}<button type="button" className="btn btn-primary" disabled={busy} onClick={reset}>{busy ? 'Resetting…' : 'Yes, reset it'}</button>
            {' '}<button type="button" className="btn" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </>
        )}
        <span className="hint demo-new">
          Demo for a different website? <Link to="/admin/clients/new?demo=1">Add a new demo client</Link> with that site and the same sign-in address: the login moves to the new demo.
        </span>
      </div>
    </section>
  );
}
